import { useState, useEffect } from 'react';
import axios from 'axios';
import { read, utils } from 'xlsx';
import { ConfigReader } from '../../../config/ConfigReader';
import { AdminApiDomain, getAdminApiUrl } from '../../../config/ApiRouting';

export interface Brand {
  termId: number;
  name: string;
  slug: string;
  taxonomy: string;
}

export interface ParsedXlsx {
  orderIds: string[];
  brands: string[];
  fileName: string;
  totalRows: number;
}

export interface ParsedMultiXlsx {
  files: ParsedXlsx[];
  mergedOrderIds: string[];
  mergedBrands: string[];
}

interface SubmitPayload {
  orderIds: string[];
  orderSegment: 'marketing' | 'partnership';
  brandSlug: string | null;
  brandName: string | null;
  createdBy: string;
}

interface SubmitResponse {
  success: boolean;
  message: string;
  insertedCount: number;
}

const ORDER_ID_COLUMNS = ['merchant_order_no', '\uC694\uCCADID', 'order_id', 'orderId', 'Order ID', 'CustomerOrderNo.'];
const BRAND_COLUMN_PATTERN = /^brand\d+$/i;
const REMARK_COLUMNS = ['\uBE44\uACE0', 'remark', 'remarks'];

export const useOrderSegment = () => {
  const config = ConfigReader.getInstance().getConfig();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get<Brand[]>(`${config.apiUrl}/product/taxonomy/brands`, { timeout: 10000 });
        setBrands(response.data);
      } catch (error) {
        console.error('Failed to fetch brands:', error);
      } finally {
        setBrandsLoading(false);
      }
    };
    fetchBrands();
  }, [config.apiUrl]);

  const parseXlsx = async (file: File): Promise<ParsedXlsx> => {
    const buffer = await file.arrayBuffer();
    const data = new Uint8Array(buffer);
    const workbook = read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
      throw new Error('XLSX file has no sheets');
    }
    const firstSheet = workbook.Sheets[sheetName]!;
    const rawRows: any[][] = utils.sheet_to_json(firstSheet, { header: 1 });

    // Scan rows to find the header row containing an order ID column
    let headerRowIndex = -1;
    let orderIdColIndex = -1;
    for (let i = 0; i < Math.min(20, rawRows.length); i++) {
      const row = rawRows[i];
      if (!row) {
        continue;
      }
      const colIndex = row.findIndex((cell: any) => {
        const cellValue = String(cell ?? '')
          .trim()
          .toLowerCase();
        return ORDER_ID_COLUMNS.some((col) => col.toLowerCase() === cellValue);
      });
      if (colIndex !== -1) {
        headerRowIndex = i;
        orderIdColIndex = colIndex;
        break;
      }
    }

    if (headerRowIndex === -1) {
      const allHeaders = rawRows.slice(0, 5).flat().filter(Boolean).map(String);
      throw new Error(
        `No order ID column found. Expected one of: ${ORDER_ID_COLUMNS.join(', ')}. Found: ${allHeaders.join(', ')}`,
      );
    }

    const headerRow = rawRows[headerRowIndex]!;
    const brandColIndices: number[] = [];
    const remarkColIndices: number[] = [];
    headerRow.forEach((cell: any, i: number) => {
      const cellValue = String(cell ?? '').trim();
      if (BRAND_COLUMN_PATTERN.test(cellValue)) {
        brandColIndices.push(i);
      }
      if (REMARK_COLUMNS.some((r) => r.toLowerCase() === cellValue.toLowerCase())) {
        remarkColIndices.push(i);
      }
    });

    const dataRows = rawRows.slice(headerRowIndex + 1);
    const orderIds = [
      ...new Set(
        dataRows.map((row) => String(row[orderIdColIndex] ?? '').trim()).filter((id) => id !== '' && id !== '0'),
      ),
    ];

    if (orderIds.length === 0) {
      throw new Error('Order ID column found but no valid order IDs extracted');
    }

    const brands = [
      ...new Set(
        dataRows.flatMap((row) => {
          const values: string[] = [];
          for (const ci of brandColIndices) {
            const v = String(row[ci] ?? '').trim();
            if (v && v !== '0') {
              values.push(v);
            }
          }
          for (const ci of remarkColIndices) {
            const v = String(row[ci] ?? '').trim();
            if (v && v !== '0') {
              values.push(v);
            }
          }
          return values;
        }),
      ),
    ];

    return {
      orderIds,
      brands,
      fileName: file.name,
      totalRows: dataRows.length,
    };
  };

  const parseMultiXlsx = async (files: File[]): Promise<ParsedMultiXlsx> => {
    const results = await Promise.all(files.map(parseXlsx));
    const mergedOrderIds = [...new Set(results.flatMap((r) => r.orderIds))];
    const mergedBrands = [...new Set(results.flatMap((r) => r.brands))];
    return { files: results, mergedOrderIds, mergedBrands };
  };

  const submit = async (payload: SubmitPayload): Promise<SubmitResponse> => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${getAdminApiUrl(AdminApiDomain.ORDER)}/orders/segment`, payload, {
        timeout: 30000,
        headers: { 'x-api-key': config.apiKey },
      });
      return response.data as SubmitResponse;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        return error.response.data as SubmitResponse;
      }
      return {
        success: false,
        message: `Request failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        insertedCount: 0,
      };
    } finally {
      setSubmitting(false);
    }
  };

  return {
    brands,
    brandsLoading,
    parseXlsx,
    parseMultiXlsx,
    submit,
    submitting,
  };
};
